/**
 * Compares two objects and returns the list of changed fields
 * @param {Object} original - Original object
 * @param {Object} updated - Updated object
 * @param {Array} excludeFields - Fields to exclude from comparison (e.g., _id, history)
 * @returns {Array} - Array of changed field names
 */
export function detectChangedFields(original, updated, excludeFields = ['_id', 'history', 'updatedAt', 'createdAt']) {
  const changedFields = []

  // Get all keys from both objects
  const allKeys = new Set([...Object.keys(original || {}), ...Object.keys(updated || {})])

  allKeys.forEach(key => {
    // Skip excluded fields
    if (excludeFields.includes(key)) return

    const originalValue = original?.[key]
    const updatedValue = updated?.[key]

    // Handle different types of values
    if (isObject(originalValue) && isObject(updatedValue)) {
      // Deep comparison for objects
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        changedFields.push(key)
      }
    } else if (Array.isArray(originalValue) && Array.isArray(updatedValue)) {
      // Array comparison
      if (JSON.stringify(originalValue.sort()) !== JSON.stringify(updatedValue.sort())) {
        changedFields.push(key)
      }
    } else {
      // Direct comparison for primitives
      if (originalValue !== updatedValue) {
        changedFields.push(key)
      }
    }
  })

  return changedFields
}

/**
 * Checks if a value is a plain object
 */
function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Creates a history entry for tracking changes
 * @param {String} userId - ID of user making the change
 * @param {String} action - Action performed (e.g., 'update', 'draft_update', 'create')
 * @param {Array} changedFields - Array of field names that changed
 * @returns {Object} - History entry object
 */
export function createHistoryEntry(userId, action, changedFields = []) {
  return {
    timestamp: new Date(),
    userId,
    action,
    changedFields,
  }
}
